import moment from "moment";

import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MedicalNotesConfigurationHelper } from "@/Patient/Helpers/MedicalNotesConfigurationHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";

import styles from "./EncountersListComponent.module.scss";

interface EncounterListProps {
    summaryModel: IPatientMedicalSummaryModel,
    noteModel: IPatientMedicalNoteModel,
    displayOnlyApprovedEncounters?: boolean;
    encounterClickFn: (encounterId: string) => any,
    context: any
}

function EncountersListComponent(props: EncounterListProps): JSX.Element {
    const { summaryModel, noteModel, encounterClickFn, context } = props;
    const displayVisitDiagnosis = MedicalNotesConfigurationHelper.getSpecificConfigAndUpdateNotesFilterSettings()?.DisplayVisitDiagnosis;
    const defaultClass = "flex-container medical-note ";

    const encountersRender = summaryModel.get("DisplayedEncounters")
        .toArray()
        .filter(encounter => !props.displayOnlyApprovedEncounters || encounter.IsSignedOff)
        .map((encounter: Encounter, idx: number) => {
            let relatedEmployeesRender: JSX.Element[] = [];

            const markedForAutoApproval = encounter.AutoApprove && encounter.SignedOffDate == null;

            const unsignedClass = !encounter.SignedOffDate
                ? (markedForAutoApproval ? styles.approvedNoteText : "red-text")
                : "";

            const autoApproveClass = markedForAutoApproval ? styles.approvedNoteText : "";

            encounter.EncounterRelatedEmployees.sortByProperty(p => Date.parse(p.FirstContribution)).map((e, idx) => {
                let employee = cacheStore.GetCliniciansPersonsByContactFacetId(e.EmployeeId);

                if (!(employee == null))
                    relatedEmployeesRender.push(<p key={"emp" + idx} className={autoApproveClass + " ellipsis"}> {employee.NameWithPrefix}</p>);
                else
                    relatedEmployeesRender.push(<p key={"emp" + idx} />);
            });

            let selectedClass = "";

            if (!(noteModel == null)) {
                let noteEncounter = noteModel.get("Encounter");
                if (!(noteEncounter == null)) {
                    if (noteEncounter.Id == encounter.Id)
                        selectedClass = "selected";
                }
            }

            let noteElipsisStyle = {
                overflowX: "hidden",
                display: "block",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            };

            const diagnoses = cacheStore.ClinicSettings.EnableDiagnosisOnPatientNoteTile && displayVisitDiagnosis
                ? PatientMedicalSummaryHelper.getEncounterDiagnosesForNoteTile(encounter.Id, summaryModel)
                : null;

            const diagnosisDefaultShownCount = 3;
            let diagnosesToBeShown: string[], restCount: number, diagnosesTooltipText: string;

            if (diagnoses && diagnoses.any()) {
                if (diagnoses.length > diagnosisDefaultShownCount) {
                    diagnosesToBeShown = diagnoses.slice(0, diagnosisDefaultShownCount);
                    const slicedArr = diagnoses.slice(3);
                    diagnosesTooltipText = slicedArr.join(", \n");
                    restCount = slicedArr.length;
                }
                else {
                    diagnosesToBeShown = diagnoses;
                }
            }

            return (
                <div className="pointer" key={"__a" + idx} onClick={encounterClickFn.bind(context, encounter.Id)}>
                    <div className={"med-note " + selectedClass} note-list-item-id={encounter.Id}>
                        <div className={defaultClass}>
                            <div className="flx-col x">
                                <p className={"ellipsisMedium " + unsignedClass}>
                                    <strong>
                                        {cacheStore.GetLocalizedValueByLookupId(encounter.ConsultationTypeLookupId)}
                                    </strong>
                                </p>
                                {encounter.CostCenterId && (
                                    <div title={cacheStore.GetCostCenterNameById(encounter.CostCenterId)} className={autoApproveClass + " margin-note-list"}>
                                        <span className="italicFont">
                                            {limitTextLength(PrivateBillCostCenter.GetDisplayName(cacheStore.GetCostCenterById(encounter.CostCenterId)), 20)}
                                        </span>
                                    </div>
                                )}
                                {relatedEmployeesRender}
                            </div>
                            <div className="flx-col y">
                                <p className={"ellipsisMedium " + unsignedClass}><span>{moment(encounter.ConsultationDate).format("YYYY/MM/DD")}</span></p>
                            </div>
                        </div>
                        <div key={"encline" + idx} className={defaultClass + autoApproveClass}>
                            {JSXHelper.BreakStringIntoParagraphs(encounter.VisitPurpose, true, noteElipsisStyle, 5)}
                        </div>
                        {diagnoses?.any() && (
                            <div className={`flex-container medical-note ${styles.diagnosesInfoContainer}`}>
                                <div className={`flex-container ${styles.diagnosesInfoTextContainer} italicFont`}>{diagnosesToBeShown.map((diagnosis, index) => <p key={index}>{limitTextLength(diagnosis, 30)}</p>)}</div>
                                {restCount && (
                                    <div>
                                        <p title={diagnosesTooltipText}>+{restCount}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            );
        });

    return (
        <div className="note-collection scrollable" data-cy="medicalNotes">
            {encountersRender}
        </div>
    );
}

export default EncountersListComponent;
